import { useContext, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { CashtagStatus } from '@cash-web/protos/squareup/franklin/api/cashtag_status.pb'
import {
  GetCashtagStatusResponse,
  SetCashtagResponse,
  SetCashtagResponseStatus,
} from '@cash-web/protos/squareup/franklin/app/cashtag.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetCashtag } from '@cash-web/shared-data-access-api/react-query/useMutationSetCashtag'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useCurrencySymbol } from '@cash-web/shared-feature-currency-symbol'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

export const useBlockerCashtag = () => {
  const { t } = useTranslation()
  const { setError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const { mutateAsync: setCashtag } = useMutationSetCashtag({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const currencySymbol = useCurrencySymbol()
  const { profile } = useAuth()
  // eslint-disable-next-line @nx/workspace/no-new-useauth-profile-fields -- use useProfileCashtag from @cash-web/shared-data-access-core-profile
  const mode = profile?.cashtag ? 'edit' : 'add'
  const [showSupport, setShowSupport] = useState(false)

  const onSetCashtag = (response: SetCashtagResponse) => {
    if (mode === 'edit' && response.status === SetCashtagResponseStatus.FAILURE) {
      setShowSupport(true)
    }
    return response
  }

  const title =
    mode === 'edit'
      ? t('Edit your {currencySymbol}cashtag', { context: 'Title text.', currencySymbol })
      : t('Choose a {currencySymbol}cashtag', { currencySymbol, context: 'Title text.' })

  const defaultEditSecondaryText = t(
    'You can only choose a new {currencySymbol}cashtag twice. You can always use any previous {currencySymbol}cashtag.',
    { context: 'Secondary text.', currencySymbol }
  )

  const [secondaryText, setSecondaryText] = useState<string | undefined>(
    mode === 'edit'
      ? defaultEditSecondaryText
      : t('This is your unique name for getting paid by anyone.', { context: 'Subtitle text.' })
  )

  const onGetCashStatus = (cashTagStatus: GetCashtagStatusResponse) => {
    if (mode === 'edit') {
      // eslint-disable-next-line @nx/workspace/no-new-useauth-profile-fields -- use useProfileCashtag from @cash-web/shared-data-access-core-profile
      if (cashTagStatus.cashtag_candidate === profile?.cashtag) {
        setSecondaryText(defaultEditSecondaryText)
      } else if (cashTagStatus.confirm_message) {
        setSecondaryText(cashTagStatus.confirm_message)
      }
    }
  }

  const getButtonText = () => {
    if (showSupport) {
      return t('Contact Cash App Support', { context: 'Button label.' })
    } else if (mode === 'edit') {
      return t('Save', { context: 'Button label.' })
    } else if (mode === 'add') {
      return t('Add', { context: 'Button label to add a cashtag.' })
    }
    return undefined
  }

  const onSubmit = async ({ cashtag }: { cashtag: string }) => {
    const errorMessages = {
      INVALID: t('Sorry, that cashtag is not available. Try another?', { context: 'Error text.' }),
      CASHTAG_STATUS_FAILURE: t('Sorry, that cashtag is not available. Try another?', { context: 'Error text.' }),
      FAILURE: t('Sorry there was an error, try another.', { context: 'Error text. unknown error message' }),
    }

    await handleBlockerResponse({
      promisedBlockerUpdate: setCashtag({ cashtag }).then(onSetCashtag),
      errors: errorMessages,
      isResponseSuccess: response => response.status === SetCashtagResponseStatus.SUCCESS,
      onFailure: response => {
        // Handle the special case where we need to check cashtag_status
        if (
          response?.status === SetCashtagResponseStatus.CASHTAG_STATUS_FAILURE &&
          response.cashtag_status === CashtagStatus.INVALID_CASHTAG
        ) {
          setError(t('Sorry, that cashtag is not available. Try another?', { context: 'Error text.' }))
        }
        // All other cases will be handled by the errors mapping
      },
    })
  }

  return { getButtonText, onGetCashStatus, onSubmit, showSupport, subtitle: secondaryText, title }
}
